import { endpoints } from 'services';
import { SERVER_URL } from 'services/endpoints';
import { refresh } from 'services/request';
import { isExaOneAccessDenied, revokeExaOneAccess } from 'services/exaOneAccessCache';

export const EXAONE_MAX_TEXT_LENGTH = 32768;

export type ExaOneRole = 'user' | 'assistant';

export interface ExaOneMessage {
  /** user: 사용자 질문, assistant: 이전 챗봇 답변 */
  role: ExaOneRole;
  content: string;
}

export interface ExaOneRateLimit {
  secondRemaining: string | null;
  minuteRemaining: string | null;
}

export interface ExaOneStreamPayload {
  /** 대화 메시지 목록 (마지막 요소가 이번 질문) */
  messages: ExaOneMessage[];
  /** 첨부 이미지 (data:image/png;base64,... 형태의 data URI, 1장만, 마지막 메시지에 첨부됨) */
  image?: string;
}

export interface ExaOneStreamCallbacks {
  /** 사고 과정 텍스트 조각 (role은 보통 첫 청크에만 내려옴) */
  onReasoning: (text: string, role?: string) => void;
  /** 최종 답변 텍스트 조각 (role은 보통 첫 청크에만 내려옴) */
  onContent: (text: string, role?: string) => void;
  /** 정상 종료 */
  onDone: () => void;
  /** 에러 발생 (업스트림 오류, 타임아웃, 동시 세션 초과 등) */
  onError: (resultCode: number, resultMessage: string) => void;
  onRateLimit?: (rateLimit: ExaOneRateLimit) => void;
}

interface ExaOneContentPart {
  type: 'text' | 'image_url';
  text?: string;
  image_url?: { url: string };
}

interface ExaOneUpstreamMessage {
  role: ExaOneRole;
  content: string | ExaOneContentPart[];
}

/** 이미지가 없으면 content는 문자열 그대로, 있으면 fo-api 네이티브 배열로 구성한다. */
const buildContent = (content: string, image?: string): string | ExaOneContentPart[] => {
  if (!image) {
    return content;
  }

  return [
    { type: 'text', text: content },
    { type: 'image_url', image_url: { url: image } },
  ];
};

const buildRequestBody = (payload: ExaOneStreamPayload): { messages: ExaOneUpstreamMessage[] } => {
  // 화면 이력은 유지하고 이번 질문을 포함한 최근 5개 메시지만 전송한다.
  const messages = payload.messages.slice(-5).map((message, index, recent) => {
    const isLastMessage = index === recent.length - 1;

    // 긴 모델 답변은 화면에 유지하고, 다음 요청의 문맥만 서버 길이 한도에 맞춘다.
    const content = message.role === 'assistant'
      ? message.content.slice(0, EXAONE_MAX_TEXT_LENGTH).replace(/[\uD800-\uDBFF]$/, '')
      : message.content;
    return {
      role: message.role,
      content: isLastMessage ? buildContent(content, payload.image) : content,
    };
  });

  return { messages };
};

interface ParsedSseEvent {
  event: string;
  data: string;
}

const parseSseBlock = (block: string): ParsedSseEvent | null => {
  const lines = block.split('\n');
  let event = 'message';
  const dataLines: string[] = [];

  lines.forEach(line => {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  });

  if (dataLines.length === 0 && event === 'message') {
    return null;
  }

  return { event, data: dataLines.join('\n') };
};

// true를 반환하면 스트림 처리를 종료한다 (done/error 수신 시).
const dispatch = (parsed: ParsedSseEvent, callbacks: ExaOneStreamCallbacks): boolean => {
  switch (parsed.event) {
    case 'rate-limit':
      callbacks.onRateLimit?.(parsed.data ? JSON.parse(parsed.data) : { secondRemaining: null, minuteRemaining: null });
      return false;
    case 'reasoning': {
      const { content, role } = JSON.parse(parsed.data);
      callbacks.onReasoning(content, role);
      return false;
    }
    case 'content': {
      const { content, role } = JSON.parse(parsed.data);
      callbacks.onContent(content, role);
      return false;
    }
    case 'done':
      callbacks.onDone();
      return true;
    case 'error': {
      const { resultCode, resultMessage } = JSON.parse(parsed.data);
      callbacks.onError(resultCode, resultMessage);
      return true;
    }
    default:
      return false;
  }
};

const readStream = async (response: Response, callbacks: ExaOneStreamCallbacks): Promise<void> => {
  if (!response.body) {
    callbacks.onError(0, 'EXAONE 응답을 읽을 수 없습니다.');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finished = false;

  try {
    while (!finished) {
      const { value, done } = await reader.read();

      if (done) {
        break;
      }

      // 프록시 등을 거치며 개행이 \r\n 으로 바뀌어도 이벤트 경계(\n\n)를 찾을 수 있도록 정규화
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');

      let boundary = buffer.indexOf('\n\n');

      while (boundary !== -1 && !finished) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const parsed = parseSseBlock(block);

        if (parsed) {
          finished = dispatch(parsed, callbacks);
        }

        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    try { await reader.cancel(); } catch { /* 연결 종료 중 오류는 기존 응답 처리를 유지한다. */ }
    reader.releaseLock();
  }

  // fo-api는 정상이면 done, 실패면 error 이벤트를 항상 보낸다. 둘 다 없이 끊기면(SSE 타임아웃, 연결 끊김 등) 비정상 종료로 본다.
  if (!finished) {
    callbacks.onError(0, 'EXAONE 응답이 중단되었습니다.');
  }
};

const execute = async (
  payload: ExaOneStreamPayload,
  callbacks: ExaOneStreamCallbacks,
  signal: AbortSignal,
  allowRetry: boolean
): Promise<void> => {
  try {
    const accessToken = localStorage.getItem('llAccessToken');
    const refreshToken = localStorage.getItem('llRefreshToken');
    const activeCallbacks = {
      ...callbacks,
      onError: (resultCode: number, resultMessage: string) => {
        callbacks.onError(resultCode, resultMessage);
        if (isExaOneAccessDenied(resultCode)) revokeExaOneAccess(accessToken, refreshToken);
      },
    };
    const response = await fetch(`${SERVER_URL}${endpoints.exaoneChatbot.send}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(buildRequestBody(payload)),
      signal,
    });

    const contentType = response.headers.get('content-type') || '';

    // 인증 실패 등으로 컨트롤러까지 못 가고 막히면 SSE가 아니라 BaseResDto(JSON)가 온다.
    if (contentType.includes('application/json')) {
      const data = await response.json();

      if (data.resultCode === 1001 && allowRetry) {
        try {
          await refresh();
        } catch {
          // 갱신 중 취소된 요청의 실패는 현재 대화의 권한 캐시를 바꾸지 않는다.
          if (signal.aborted) return;
          // 일시적인 인증 갱신 실패는 권한 회수가 아니다. 기존 채팅과 대화를 유지한다.
          callbacks.onError(0, '로그인 상태를 확인한 뒤 다시 시도해주세요.');
          return;
        }
        if (signal.aborted) return;
        await execute(payload, callbacks, signal, false);
        return;
      }

      activeCallbacks.onError(data.resultCode, data.resultMessage);
      return;
    }

    // 게이트웨이 오류 등으로 HTML/빈 응답이 오면 SSE가 아니므로 스트림으로 읽지 않는다.
    if (!response.ok || !contentType.includes('text/event-stream')) {
      activeCallbacks.onError(response.status, 'EXAONE 연동에 오류가 발생하였습니다.');
      return;
    }

    await readStream(response, activeCallbacks);
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      return;
    }

    callbacks.onError(0, 'EXAONE 연동 중 오류가 발생했습니다.');
  }
};

export default {
  /**
   * EXAONE 챗봇 질문 전송 (스트리밍)
   * 반환값은 스트림을 중단시키는 함수다.
   */
  streamChat: (payload: ExaOneStreamPayload, callbacks: ExaOneStreamCallbacks): (() => void) => {
    const controller = new AbortController();

    execute(payload, callbacks, controller.signal, true);

    return () => controller.abort();
  },
};
